import { Button, Col, Empty, Form, Pagination, Row, Skeleton, Table } from 'antd';
import { FormInstance } from 'antd/lib/form';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TableConfigurator from '../../../shared/components/TableConfigurator/TableConfigurator';
import { authFeatureConstants, GUTTOR_SPACE, GRIEVANCE_DETAIL_ROUTE } from '../../../shared/constants/commonConstants';
import { TABLE_SIZE_MIDDLE } from '../../../shared/constants/crmConstants';
import { RootState } from '../../../store';
import { resetGrievance } from '../../../store/actions/grievanceAction';
import { ApplicationDetailModel } from '../../../store/models/applicationDetailModel';
import { base64UrlEncode, base64UrlEncoded, canActivate, centeredNotification } from '../../../utils/utility';
import { fetchClaimList, searchClaimList, setSelectedClaims } from '../../../store/actions/grievanceAction';
import { clearSearchDetail, saveInPatientConfig, searchInPatientList, setPageIndex, setSortingPreference } from '../../../store/actions/inPatientAction';
import { checkIfAnyTableSearchEnabled, getTableBasedOnConfig } from '../../crm/inPatient/service/crmUtils';
import { getGrievanceTableMapping } from '../services/grievanceConstants';
import { useHistory } from 'react-router-dom';
import { Payer } from '../../../shared/models/payer.model';
import { changePayer } from '../../../store/actions/payerListAction';
import { fetchGreivanceLookUp, setAllGrievance } from '../../../store/actions/lookUpAction';
import { isEmpty } from 'lodash';
import useSetDefaultPayer from '../../../shared/hooks/useSetDefaultPayer';
import { Claim, ClaimsList } from '../../../shared/interfaces/grievanceInterfaces';

const ClaimList = (): ReactElement => {
  const blankArr = Array(10).fill(0);

  const [claimForm] = Form.useForm();
  const [searchForm] = Form.useForm();

  const dispatch = useDispatch();
  const appDetail = useSelector((state: RootState): ApplicationDetailModel => state.applicationDetail);
  const {
    selectedInPatientDetail: { filters, sortingKey, isSearchClicked, pageIndex },
  } = useSelector((state: RootState): any => state || {});
  const payerList = useSelector((state: RootState): Payer[] => state?.payerList?.allPayers || []);
  const [claimsColumns, setClaimsColumns] = useState([] as any);
  const [claimsData, setClaimsData] = useState([] as any);
  const [, setIsColumnFilter] = useState(false);
  const grievanceTableMapping = getGrievanceTableMapping(canActivate(authFeatureConstants.NEW_ADMISSION_BEHALF_OF_HOSPITALS));
  const { grievance: { selectedClaimsList } } = useSelector((state: RootState): any => state || {});
  const history = useHistory();
  const [claimDataCount, setClaimDataCount] = useState(-1);
  const defaultPayer = useSetDefaultPayer();

  useEffect((): any => {
    dispatch(resetGrievance());
    searchForm.resetFields();
    claimForm.resetFields();
    dispatch(clearSearchDetail());
    dispatch(setAllGrievance([]));
  }, []);

  useEffect((): any => {
    dispatch(fetchGreivanceLookUp((): any => { }));
  }, []);

  const onNewGrievance = (list: Claim): void => {
    const listToUpdate = selectedClaimsList
      ? Object?.keys(selectedClaimsList)?.reduce(
          (acc: ClaimsList, key: string): ClaimsList => {
            acc[key] = { ...selectedClaimsList[key], isSelected: false };
            return acc;
          },
          {} as Record<string, any>,
        )
      : {}; // If null/undefined, default to an empty object

    // Select the new claim
    listToUpdate[list?.hospActionId] = { ...list, isSelected: true };
    dispatch(setSelectedClaims(listToUpdate));
    updatePayer(list);
    const selectedClaims = Object?.keys(listToUpdate)?.filter(
      (key: string): boolean => listToUpdate[key]?.isSelected,
    );
    const encodedClaims = base64UrlEncode(selectedClaims.join('-'));
    history.push(`new-grievance/${encodedClaims}`);
  };

  const viewGrievance = (list: Claim): void => {
    const listToUpdate = selectedClaimsList
      ? Object?.keys(selectedClaimsList)?.reduce(
          (acc: ClaimsList, key: string): ClaimsList => ({
            ...acc,
            [key]: { ...selectedClaimsList[key], isSelected: false },
          }),
          {} as Record<string, any>,
        )
      : {}; // Default to empty object if selectedClaimsList is null/undefined

    listToUpdate[list?.hospActionId] = { ...list, isSelected: true };
    dispatch(setSelectedClaims(listToUpdate));
    updatePayer(list);

    const encodedRecord = base64UrlEncoded(JSON.stringify(list));
    history.push(`${GRIEVANCE_DETAIL_ROUTE}${encodedRecord}`);
  };

  useEffect((): any => {
    const filteredTable = getTableBasedOnConfig(appDetail.getIPConfig(), grievanceTableMapping);
    setClaimsColumns(
      [
        ...filteredTable,
        {
          title: 'Actions',
          dataIndex: 'actions',
          key: 'actions',
          render: (record: any, row: any): any => (
            <Col>
              {
                canActivate(authFeatureConstants?.EDIT_GRIEVANCE) &&
                <Button type="primary" size="small" className="ant-tabs-content-holder grievance-ticket-btn"
                  onClick={(): any => !isEmpty(row?.grievanceTicketId) ? viewGrievance(row) : onNewGrievance(row)}>
                  {!isEmpty(row?.grievanceTicketId) ? 'View Ticket' : 'Raise Ticket'}
                </Button>
              }
            </Col>
          ),
        },
      ]);
  }, [appDetail]);

  useEffect((): any => {
    setLoaderStateValue();
    dispatch(fetchClaimList({
      getClaimsList,
    }));
  }, [isSearchClicked]);

  useEffect((): any => {
    if (checkIfAnyTableSearchEnabled(filters, sortingKey)) {
      setIsColumnFilter(true);
    } else {
      setIsColumnFilter(false);
    }
  }, [filters, sortingKey]);

  const pageChange = (pageNumber: any): any => {
    dispatch(setPageIndex(pageNumber));
    dispatch(searchInPatientList());
  };

  const onColumnEditSave = (form: FormInstance, setIsVisible: any): any => {
    dispatch(saveInPatientConfig(form.getFieldsValue(), setIsVisible, 'grievance'));
  };

  const setLoaderStateValue = (): void => {
    setClaimDataCount(-1);
    setClaimsData(null);
  };
  const getClaimsList = (data: any): void => {
    const { claimDetails, count, isSuccess, errors = [] } = data;
    if (isSuccess) {
      claimDetails.map((detail: any, index: number): void => {
        detail.key = index;
      });
      setClaimsData(claimDetails);
      setClaimDataCount(count);
    } else {
      setClaimsData([]);
      setClaimDataCount(count);
      centeredNotification(errors.join(',\n'));
    }
  };
  const handleTableChange = (...attributes: any): any => {
    const { columnKey: key, order: value } = attributes[2];
    const columnToUpdate = claimsColumns.find(({ key: colKey }: any): boolean => colKey === key);
    columnToUpdate.sortOrder = value;
    setClaimsColumns([...claimsColumns]);
    dispatch(searchClaimList());
    dispatch(searchInPatientList());
    dispatch(setSortingPreference(value ? { key, value: String(value).toUpperCase() } : null));
  };

  const updatePayer = (payerListItem: any): any => {
    const currUniqId = `${payerListItem?.payerId}${payerListItem?.productCode || ''}`.trim();
    const selectedPayer = payerList.find((ele: Payer): boolean => {
      return ele?.uniqueIdentifier === currUniqId;
    });
    dispatch(changePayer(selectedPayer || defaultPayer));
  };

  const tableTitle = (): any => {
    return (<Row className="margin-button" gutter={GUTTOR_SPACE}>
      <Col>
        <Form
          form={searchForm}>
          <TableConfigurator
            tableMapping={grievanceTableMapping}
            saveClickHandler={onColumnEditSave}
            configuration={appDetail.getIPConfig()}
            isGrievancePage={true}
          />
        </Form>
      </Col>
    </Row>
    );
  };
  return (
    <>
      <Form
        form={claimForm}>
        <Table
          title={tableTitle}
          className="ant-tabs-content-holder grievance-tbl"
          data-testid="in-patient-list"
          size={TABLE_SIZE_MIDDLE}
          columns={claimsColumns}
          dataSource={claimsData}
          pagination={false}
          rowKey="hospActionId"
          locale={{
            emptyText: (claimDataCount) !== -1 ?
              <Empty /> :
              <div className="full-skelton-in-list">
                {blankArr.map((...params: any[]): any =>
                  <Skeleton
                    key={params[1]}
                    active={true}
                    paragraph={{ rows: 4 }}
                  />,
                )}
              </div>,
          }}
          onChange={handleTableChange}
        />
      </Form>
      {(claimDataCount > 0) && <Pagination
        data-testid="pagination"
        showSizeChanger={false}
        onChange={pageChange}
        defaultCurrent={1}
        total={claimDataCount}
        current={pageIndex}
        className="mt-md text-center"
      />}</>
  );
};
export default ClaimList;
